import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@tanstack/react-query';
import { ImagePlus, BadgeCheck } from 'lucide-react';
import { RichTextEditor } from '../RichTextEditor';
import { slaProfielOp, foutMelding, Certificering, OpleiderOnboardingState } from '../../services/opleiderOnboardingApi';
import { Kaart, StapKop, Knop, Melding } from './ui';

const CERTIFICERINGEN: { code: Certificering; label: string }[] = [
  { code: 'NRTO', label: 'NRTO' },
  { code: 'CEDEO', label: 'Cedeo' },
  { code: 'CRKBO', label: 'CRKBO' },
];

interface Afmeting { breedte: number; hoogte: number; transparant: boolean }

/** Leest afmetingen via de browser en (voor PNG) het kleurtype uit de IHDR-header. */
async function leesAfmeting(file: File): Promise<Afmeting> {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = reject;
      i.src = url;
    });
    let transparant = false;
    if (file.type === 'image/png') {
      const kop = new Uint8Array(await file.slice(0, 4096).arrayBuffer());
      const kleurType = kop[25];
      transparant = kleurType === 4 || kleurType === 6 || new TextDecoder('latin1').decode(kop).includes('tRNS');
    }
    return { breedte: img.naturalWidth, hoogte: img.naturalHeight, transparant };
  } finally {
    URL.revokeObjectURL(url);
  }
}

function tekstLengte(html: string): number {
  return html.replace(/<[^>]+>/g, '').replace(/&nbsp;/g, ' ').trim().length;
}

export const StapProfiel: React.FC<{ state: OpleiderOnboardingState; onKlaar: () => void }> = ({ state, onKlaar }) => {
  const { t } = useTranslation('pages');
  const { provider, eisen } = state;
  const [logo, setLogo] = useState<File | null>(null);
  const [omslag, setOmslag] = useState<File | null>(null);
  const [logoPreview, setLogoPreview] = useState<string | null>(provider.logo_url);
  const [omslagPreview, setOmslagPreview] = useState<string | null>(provider.omslag_url);
  const [logoFout, setLogoFout] = useState<string | null>(null);
  const [omslagFout, setOmslagFout] = useState<string | null>(null);
  const [bio, setBio] = useState(provider.bio_html ?? '');
  const [certs, setCerts] = useState<Certificering[]>(provider.certificeringen ?? []);

  useEffect(() => () => {
    if (logoPreview?.startsWith('blob:')) URL.revokeObjectURL(logoPreview);
    if (omslagPreview?.startsWith('blob:')) URL.revokeObjectURL(omslagPreview);
  }, [logoPreview, omslagPreview]);

  const kiesLogo = async (file: File | undefined) => {
    if (!file) return;
    setLogoFout(null);
    if (file.type !== 'image/png') return setLogoFout(t('opleiderWizard.profiel.logoPng', { defaultValue: 'Het logo moet een PNG-bestand zijn.' }));
    const a = await leesAfmeting(file);
    if (Math.abs(a.breedte - a.hoogte) > Math.max(a.breedte, a.hoogte) * 0.02) {
      return setLogoFout(t('opleiderWizard.profiel.logoVierkant', { defaultValue: 'Het logo moet vierkant zijn (nu {{b}}×{{h}}px).', b: a.breedte, h: a.hoogte }));
    }
    if (a.breedte < eisen.logo_min_px) {
      return setLogoFout(t('opleiderWizard.profiel.logoKlein', { defaultValue: 'Minimaal {{px}}×{{px}}px.', px: eisen.logo_min_px }));
    }
    if (!a.transparant) {
      return setLogoFout(t('opleiderWizard.profiel.logoTransparant', { defaultValue: 'Gebruik een PNG met transparante achtergrond.' }));
    }
    setLogo(file);
    setLogoPreview(URL.createObjectURL(file));
  };

  const kiesOmslag = async (file: File | undefined) => {
    if (!file) return;
    setOmslagFout(null);
    if (!/^image\/(jpeg|png|webp)$/.test(file.type)) {
      return setOmslagFout(t('opleiderWizard.profiel.omslagType', { defaultValue: 'Gebruik een JPG-, PNG- of WebP-bestand.' }));
    }
    const a = await leesAfmeting(file);
    if (a.breedte < eisen.omslag_min_breedte || a.breedte / a.hoogte < eisen.omslag_min_ratio) {
      return setOmslagFout(
        t('opleiderWizard.profiel.omslagPanorama', {
          defaultValue: 'Gebruik een panorama van minimaal {{min}}px breed en verhouding 2,5:1 (nu {{b}}×{{h}}px).',
          min: eisen.omslag_min_breedte,
          b: a.breedte,
          h: a.hoogte,
        })
      );
    }
    setOmslag(file);
    setOmslagPreview(URL.createObjectURL(file));
  };

  const opslaan = useMutation({
    mutationFn: () => slaProfielOp({ logo, omslag, bio_html: bio, certificeringen: certs }),
    onSuccess: onKlaar,
  });

  const bioLengte = tekstLengte(bio);
  const compleet = !!logoPreview && !!omslagPreview && bioLengte >= 150;

  return (
    <Kaart>
      <StapKop
        nummer={2}
        titel={t('opleiderWizard.profiel.titel', { defaultValue: 'Instituutprofiel & bio' })}
        intro={t('opleiderWizard.profiel.intro', {
          defaultValue: 'Dit is uw visitekaartje in de Academy: medewerkers en professionals zien precies deze header, bio en keurmerken.',
        })}
      />

      {/* Live preview van de Laag C-header */}
      <div className="relative mb-8 overflow-hidden rounded-2xl ring-1 ring-white/10" aria-label={t('opleiderWizard.profiel.preview', { defaultValue: 'Voorbeeld profielheader' })}>
        <div className="relative h-36 sm:h-44">
          {omslagPreview ? (
            <img src={omslagPreview} alt="" className="absolute inset-0 h-full w-full object-cover" />
          ) : (
            <div className="absolute inset-0 bg-[radial-gradient(120%_120%_at_20%_0%,#1d4ed855_0%,#0b1020_55%,#05070c_100%)]" />
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-[#0B0F17] via-[#0B0F17]/50 to-transparent" />
        </div>
        <div className="relative -mt-12 flex items-end gap-4 px-5 pb-4">
          <div className="flex h-20 w-20 shrink-0 items-center justify-center overflow-hidden rounded-2xl bg-white shadow-xl ring-4 ring-[#0B0F17]">
            {logoPreview ? <img src={logoPreview} alt="" className="h-full w-full object-contain p-2" /> : <ImagePlus className="h-7 w-7 text-slate-400" />}
          </div>
          <div className="min-w-0 pb-1">
            <p className="truncate text-lg font-bold text-white">
              {provider.organisatie_naam || t('opleiderWizard.profiel.uwInstituut', { defaultValue: 'Uw instituut' })}
            </p>
            <div className="mt-1 flex flex-wrap gap-1.5">
              {certs.map((c) => (
                <span key={c} className="inline-flex items-center gap-1 rounded-full bg-emerald-400/15 px-2 py-0.5 text-[11px] font-semibold text-emerald-300 ring-1 ring-emerald-400/30">
                  <BadgeCheck className="h-3 w-3" aria-hidden="true" />
                  {CERTIFICERINGEN.find((x) => x.code === c)?.label}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="grid gap-5 sm:grid-cols-[180px_1fr]">
        <Upload
          label={t('opleiderWizard.profiel.logo', { defaultValue: 'Opleiders-logo' })}
          hint={t('opleiderWizard.profiel.logoHint', { defaultValue: 'Vierkant, transparante PNG, min. {{px}}px', px: eisen.logo_min_px })}
          accept="image/png"
          fout={logoFout}
          gekozen={!!logoPreview}
          onKies={kiesLogo}
        />
        <Upload
          label={t('opleiderWizard.profiel.omslag', { defaultValue: 'Omslagafbeelding (header)' })}
          hint={t('opleiderWizard.profiel.omslagHint', { defaultValue: 'Panorama, min. {{min}}px breed, verhouding ≥ 2,5:1 (bijv. 1920×640)', min: eisen.omslag_min_breedte })}
          accept="image/jpeg,image/png,image/webp"
          fout={omslagFout}
          gekozen={!!omslagPreview}
          onKies={kiesOmslag}
        />
      </div>

      <div className="mt-6">
        <p className="mb-1.5 text-sm font-medium text-slate-200">{t('opleiderWizard.profiel.bio', { defaultValue: 'Uitgebreide bio & expertise' })}</p>
        <p className="mb-2 text-xs text-slate-500">
          {t('opleiderWizard.profiel.bioHint', { defaultValue: 'Geschiedenis, visie en expertise van uw instituut. Minimaal 150 tekens.' })}
        </p>
        <div className="overflow-hidden rounded-lg bg-white text-slate-900">
          <RichTextEditor content={bio} onChange={setBio} simple minHeight={180} />
        </div>
        <p className={`mt-1 text-right text-xs ${bioLengte >= 150 ? 'text-emerald-400' : 'text-slate-500'}`}>{bioLengte} / 150</p>
      </div>

      <fieldset className="mt-6">
        <legend className="mb-2 text-sm font-medium text-slate-200">{t('opleiderWizard.profiel.certificeringen', { defaultValue: 'Certificeringen (officiële keurmerken)' })}</legend>
        <div className="grid gap-3 sm:grid-cols-3">
          {CERTIFICERINGEN.map(({ code, label }) => {
            const aan = certs.includes(code);
            return (
              <label
                key={code}
                className={`flex cursor-pointer items-center gap-3 rounded-xl border px-4 py-3 text-sm font-semibold transition ${
                  aan ? 'border-emerald-400/50 bg-emerald-400/10 text-emerald-200' : 'border-white/10 text-slate-300 hover:bg-white/5'
                }`}
              >
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-emerald-400"
                  checked={aan}
                  onChange={() => setCerts((c) => (aan ? c.filter((x) => x !== code) : [...c, code]))}
                />
                {label}
              </label>
            );
          })}
        </div>
        <p className="mt-2 text-xs text-slate-500">
          {t('opleiderWizard.profiel.certHint', { defaultValue: 'Vink alleen keurmerken aan waarvoor u een geldige registratie heeft; BeLagom kan hierom vragen.' })}
        </p>
      </fieldset>

      {opslaan.isError && (
        <div className="mt-5">
          <Melding>{foutMelding(opslaan.error, t('opleiderWizard.profiel.fout', { defaultValue: 'Opslaan mislukt.' }))}</Melding>
        </div>
      )}

      <div className="mt-6 flex justify-end">
        <Knop bezig={opslaan.isPending} disabled={!compleet} onClick={() => opslaan.mutate()}>
          {t('opleiderWizard.profiel.submit', { defaultValue: 'Profiel opslaan & verder' })}
        </Knop>
      </div>
    </Kaart>
  );
};

const Upload: React.FC<{
  label: string;
  hint: string;
  accept: string;
  fout: string | null;
  gekozen: boolean;
  onKies: (f: File | undefined) => void;
}> = ({ label, hint, accept, fout, gekozen, onKies }) => (
  <div>
    <p className="mb-1.5 text-sm font-medium text-slate-200">{label}</p>
    <label
      className={`flex h-28 cursor-pointer flex-col items-center justify-center gap-1 rounded-xl border-2 border-dashed px-3 text-center text-xs transition focus-within:ring-2 focus-within:ring-sky-400 ${
        fout ? 'border-rose-500/60 text-rose-300' : gekozen ? 'border-emerald-400/40 text-emerald-300' : 'border-white/15 text-slate-400 hover:border-sky-400/50'
      }`}
    >
      <ImagePlus className="h-5 w-5" aria-hidden="true" />
      <span>{hint}</span>
      <input
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(e) => {
          onKies(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </label>
    {fout && <p className="mt-1 text-xs text-rose-400" role="alert">{fout}</p>}
  </div>
);
